import { Match, Show, Switch, onCleanup, onMount } from "solid-js"
import { Box, Center } from "styled-system/jsx"

interface VideoPlayerProps {
  provider?: 'vimeo' | 'youtube' | 'dailymotion' | null;
  provider_id?: string | null;
  onFinished?: () => void;
}

export default function VideoPlayer(props: VideoPlayerProps) {
  const handleMessage = (event: MessageEvent) => {
    // Security check: Ensure the message is from a trusted provider
    if (!event.origin.includes("youtube.com") && !event.origin.includes("vimeo.com") && !event.origin.includes("dailymotion.com")) return;

    try {
      const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;

      // YouTube: State 0 means "ended"
      if (data.event === "infoDelivery" && data.info?.playerState === 0) {
        props.onFinished?.();
      }

      // Vimeo: Listen for the "finish" event
      if (data.event === "finish" || data.method === "onFinish") {
        props.onFinished?.();
      }

      // Dailymotion: fires "video_end" via its postMessage API
      if (data.event === "video_end") {
        props.onFinished?.();
      }
    } catch (e) {
      // Ignore non-JSON messages
    }
  };

  onMount(() => {
    window.addEventListener("message", handleMessage);
  });

  onCleanup(() => {
    window.removeEventListener("message", handleMessage);
  });

  // Tell the Vimeo iframe we want to subscribe to the 'finish' event
  const handleVimeoLoad = (e: Event) => {
    const iframe = e.currentTarget as HTMLIFrameElement;
    if (iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        JSON.stringify({ method: 'addEventListener', value: 'finish' }),
        'https://player.vimeo.com'
      );
    }
  };

  // YouTube sometimes requires a "listening" ping to start broadcasting infoDelivery
  const handleYouTubeLoad = (e: Event) => {
    const iframe = e.currentTarget as HTMLIFrameElement;
    if (iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: 'listening', id: 1 }),
        'https://www.youtube.com'
      );
    }
  };

  return (
    <Show when={props.provider && props.provider_id} fallback={<Center color="white" h="100%">Unknown Provider</Center>}>
      <Box
        position="relative"
        width="100%"
        aspectRatio="16 / 9"
        maxHeight="100%"
        maxWidth="calc((90dvh - 180px) * 1.77)"
        mx="auto"
      >
        <Switch fallback={<Center color="white" h="100%">Unknown Provider</Center>}>
          <Match when={props.provider === 'vimeo'}>
            <iframe
              onLoad={handleVimeoLoad}
              title="Vimeo video player"
              src={`https://player.vimeo.com/video/${props.provider_id}?api=1`}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
              allow="autoplay; fullscreen; picture-in-picture"
              allowfullscreen
            />
          </Match>
          <Match when={props.provider === 'youtube'}>
            <iframe
              onLoad={handleYouTubeLoad}
              title="YouTube video player"
              src={`https://www.youtube.com/embed/${props.provider_id}?enablejsapi=1`}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
              allow="autoplay; fullscreen; picture-in-picture"
              allowfullscreen
            />
          </Match>
          <Match when={props.provider === 'dailymotion'}>
            <iframe
              title="Dailymotion video player"
              src={`https://www.dailymotion.com/embed/video/${props.provider_id}?api=postMessage`}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
              allow="autoplay; fullscreen; picture-in-picture"
              allowfullscreen
            />
          </Match>
        </Switch>
      </Box>
    </Show>
  )
}
